import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import type { AutomationRule, FormTemplate } from '@/api/generated/client';
import {
  MAX_CONDITIONS,
  OPERATOR_OPTIONS,
  SOURCE_OPTIONS,
  US_STATE_OPTIONS,
  type ConditionField,
  type ConditionOperator,
  type ConditionFormState,
  type RuleFormState,
} from '@/lib/automations';

const FIELD_OPTIONS: Array<{ value: ConditionField; label: string }> = [
  { value: 'source', label: 'Source' },
  { value: 'feePercentage', label: 'Fee' },
  { value: 'state', label: 'State' },
];

interface AutomationBuilderDialogProps {
  isOpen: boolean;
  editingRule: AutomationRule | null;
  form: RuleFormState;
  templates: FormTemplate[];
  isSaving: boolean;
  canSave: boolean;
  onOpenChange: (open: boolean) => void;
  onFormChange: (next: RuleFormState | ((prev: RuleFormState) => RuleFormState)) => void;
  onUpdateCondition: (conditionId: string, patch: Partial<ConditionFormState>) => void;
  onChangeConditionField: (conditionId: string, field: ConditionField) => void;
  onAddCondition: () => void;
  onRemoveCondition: (conditionId: string) => void;
  onSave: () => Promise<void>;
}

export function AutomationBuilderDialog({
  isOpen,
  editingRule,
  form,
  templates,
  isSaving,
  canSave,
  onOpenChange,
  onFormChange,
  onUpdateCondition,
  onChangeConditionField,
  onAddCondition,
  onRemoveCondition,
  onSave,
}: AutomationBuilderDialogProps) {
  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>{editingRule ? 'Edit Automation' : 'New Automation'}</DialogTitle>
          <DialogDescription>
            Choose a trigger, optional conditions, and an action.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="automation-name">Name</Label>
            <Input
              id="automation-name"
              value={form.name}
              placeholder="e.g. Send intake form for ClaimPredict claims"
              maxLength={200}
              onChange={(event) =>
                onFormChange((prev) => ({ ...prev, name: event.target.value }))
              }
            />
          </div>

          <div className="space-y-2">
            <Label>When</Label>
            <Select value="claim_created" disabled>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="claim_created">A claim is created</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>If (optional)</Label>
            {form.conditions.length === 0 && (
              <p className="text-xs text-muted-foreground">
                No conditions — this runs for every new claim. Add conditions to narrow it down.
              </p>
            )}
            <div className="space-y-2">
              {form.conditions.map((condition) => (
                <div key={condition.id} className="flex items-start gap-2">
                  <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
                    <Select
                      value={condition.field}
                      onValueChange={(value) => onChangeConditionField(condition.id, value as ConditionField)}
                    >
                      <SelectTrigger className="flex-1 min-w-[7.5rem]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {FIELD_OPTIONS.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>

                    <Select
                      value={condition.operator}
                      onValueChange={(value) =>
                        onUpdateCondition(condition.id, { operator: value as ConditionOperator })
                      }
                    >
                      <SelectTrigger className="flex-1 min-w-[6.5rem]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {OPERATOR_OPTIONS[condition.field].map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>

                    {condition.field === 'source' && (
                      <Select
                        value={condition.value}
                        onValueChange={(value) => onUpdateCondition(condition.id, { value })}
                      >
                        <SelectTrigger className="flex-1 min-w-[7.5rem]">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {SOURCE_OPTIONS.map((option) => (
                            <SelectItem key={option.value} value={option.value}>
                              {option.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}

                    {condition.field === 'state' && (
                      <Select
                        value={condition.value}
                        onValueChange={(value) => onUpdateCondition(condition.id, { value })}
                      >
                        <SelectTrigger className="flex-1 min-w-[7.5rem]">
                          <SelectValue placeholder="Select state" />
                        </SelectTrigger>
                        <SelectContent>
                          {US_STATE_OPTIONS.map((option) => (
                            <SelectItem key={option.value} value={option.value}>
                              {option.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}

                    {condition.field === 'feePercentage' && (
                      <div className="relative flex-1 min-w-[6.5rem]">
                        <Input
                          type="number"
                          min={0}
                          max={100}
                          step="any"
                          value={condition.value}
                          placeholder="e.g. 20"
                          onChange={(event) => onUpdateCondition(condition.id, { value: event.target.value })}
                        />
                        <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-sm text-muted-foreground">
                          %
                        </span>
                      </div>
                    )}
                  </div>

                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="shrink-0"
                    onClick={() => onRemoveCondition(condition.id)}
                    aria-label="Remove condition"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
            {form.conditions.length < MAX_CONDITIONS && (
              <Button type="button" variant="outline" size="sm" onClick={onAddCondition}>
                Add condition
              </Button>
            )}
            {form.conditions.length > 1 && (
              <p className="text-xs text-muted-foreground">All conditions must match (AND).</p>
            )}
          </div>

          <div className="space-y-2">
            <Label>Then</Label>
            <Select
              value={form.formTemplateId}
              onValueChange={(value) => onFormChange((prev) => ({ ...prev, formTemplateId: value }))}
            >
              <SelectTrigger>
                <SelectValue placeholder="Send form… (choose a template)" />
              </SelectTrigger>
              <SelectContent>
                {templates.map((template) => (
                  <SelectItem key={template.id} value={String(template.id)}>
                    Send &quot;{template.name}&quot;
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              The form is emailed to the homeowner using the claim&apos;s insured email. If no
              email is on file, you&apos;ll get a notification instead.
            </p>
          </div>

          <div className="flex items-center gap-2">
            <Switch
              id="automation-active"
              checked={form.isActive}
              onCheckedChange={(checked) =>
                onFormChange((prev) => ({ ...prev, isActive: checked }))
              }
            />
            <Label htmlFor="automation-active">Active</Label>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSaving}>
            Cancel
          </Button>
          <Button onClick={() => void onSave()} disabled={!canSave}>
            {isSaving ? 'Saving…' : editingRule ? 'Save Changes' : 'Create Automation'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
